import { SPACE, T } from '@/design/tokens';
import type { ReactNode } from 'react';

interface EmptyStateProps {
  /** Primary empty-state message. Rendered as muted body text — never italic. */
  children: ReactNode;
  /** Optional secondary line (hint, CTA prompt). Rendered smaller + muted. */
  hint?: ReactNode;
}

/**
 * EmptyState — inline empty-list placeholder.
 *
 * Replaces the one-line `<p style={{ fontStyle: 'italic' }}>` pattern.
 * Never italic — italic was the "style later" tell. Copy stays as-is;
 * Ogilvy polish is a later pass.
 *
 * Adopted in:
 *   - ServiceLog (no entries yet)
 *   - ProjectsPage (no projects, no loose tasks, no tasks in a section)
 *   - ServiceEntryDetail (entry not found)
 *   - InsightsPage (nothing waiting to seal)
 *
 * GarageEmptyState remains as the full-page first-run variant and is NOT
 * replaced by this component.
 */
export function EmptyState({ children, hint }: EmptyStateProps) {
  return (
    <div>
      <p style={{ color: T.textMuted, margin: 0 }}>{children}</p>
      {hint ? (
        <p style={{ color: T.textMuted, fontSize: 13, marginTop: SPACE.xs, marginBottom: 0 }}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
